import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const money = (v) => (v == null || isNaN(v) ? "0.00" : Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const TABS = ["Orders", "Invoices", "Outstanding", "Audit Trail"];
const ORDERS_COLS = ["Order #", "Date", "PO #", "Sales Person", "Value ₹", "Order Status", "Dispatch Status", "Payment"];
const INVOICES_COLS = ["Invoice #", "Date", "Order #", "Taxable ₹", "GST ₹", "Total ₹", "Received ₹", "Balance ₹", "Payment"];
const AUDIT_COLS = ["When", "Who", "Role", "Field", "Old Value", "New Value"];

function outstandingValueClass(label, value, creditExceeded) {
  if (label === "Total Outstanding" && value > 0) return "text-signal-amber";
  if (label === "Available Credit" && creditExceeded) return "text-signal-red";
  return "";
}

export default function CustomerDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("Orders");
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [outstanding, setOutstanding] = useState(null);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get(`/customers/${code}`).then((r) => setC(r.data));
    api.get(`/customers/${code}/orders`).then((r) => setOrders(r.data));
    api.get(`/customers/${code}/invoices`).then((r) => setInvoices(r.data));
    api.get(`/customers/${code}/outstanding`).then((r) => setOutstanding(r.data));
    api.get(`/customers/${code}/audit`).then((r) => setAudit(r.data));
  }, [code]);

  if (!c) return <div className="p-6 text-muted-foreground">Loading…</div>;
  const b = c.billing || {};

  const Th = ({ cols }) => (
    <thead><tr className="border-b border-border text-left">
      {cols.map((h) => <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>)}
    </tr></thead>
  );

  return (
    <div className="space-y-5" data-testid="customer-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/customers")} data-testid="back-to-customers"><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase">{c.name} <span className="metric text-primary text-lg">{c.code}</span></h1>
          <p className="text-xs text-muted-foreground">{c.customer_type || "—"} · {b.address || ""} {b.city || ""} {b.state || ""} {b.pin || ""} · GSTIN {b.gstin || "—"} · PAN {b.pan || "—"}</p>
          <p className="text-xs text-muted-foreground">Contact: {b.contact || "—"} · {b.mobile || "—"} · {b.email || "—"} · Payment: {c.payment_terms || "—"} · Credit: ₹{money(c.credit_limit)} / {c.credit_days || 0} days · Sales: {c.sales_person || "—"}</p>
        </div>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-primary/15 text-primary" data-testid="detail-status">{c.status}</span>
      </div>

      {(c.ship_tos || []).length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4" data-testid="detail-shiptos">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Shipping Addresses</p>
          <div className="grid md:grid-cols-2 gap-2">
            {c.ship_tos.map((s, i) => (
              <div key={`${s.name || "address"}-${s.pin || i}`} className="border border-border/60 rounded p-2 text-xs">
                <span className="font-semibold">{s.name || `Address ${i + 1}`}</span>
                {s.default && <span className="ml-2 text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-signal-green/15 text-signal-green">default</span>}
                <p className="text-muted-foreground">{[s.address, s.city, s.state, s.pin].filter(Boolean).join(", ") || "—"}</p>
                <p className="text-muted-foreground">{[s.contact, s.mobile, s.gstin ? `GSTIN ${s.gstin}` : null].filter(Boolean).join(" · ") || "—"}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 border-b border-border" data-testid="detail-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase().replace(" ", "-")}`}
            className={`px-4 py-2 text-sm font-medium transition-colors ${tab === t ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}>{t}</button>
        ))}
      </div>

      {tab === "Orders" && (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="customer-orders-table">
            <Th cols={ORDERS_COLS} />
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_number} className="border-b border-border/50" data-testid={`cust-order-${o.order_number}`}>
                  <td className="px-4 py-2.5"><button className="metric text-primary underline" onClick={() => navigate(`/orders/${o.order_number}`)}>{o.order_number}</button></td>
                  <td className="px-4 py-2.5 metric text-xs">{o.order_date ? new Date(o.order_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{o.po_number || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{o.sales_person || "—"}</td>
                  <td className="px-4 py-2.5 metric">{money(o.order_value)}</td>
                  <td className="px-4 py-2.5 text-xs uppercase">{o.order_status || "—"}</td>
                  <td className="px-4 py-2.5 text-xs uppercase">{o.dispatch_status || "—"}</td>
                  <td className="px-4 py-2.5 text-xs uppercase">{o.payment_status}</td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No orders yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "Invoices" && (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="customer-invoices-table">
            <Th cols={INVOICES_COLS} />
            <tbody>
              {invoices.map((i) => (
                <tr key={i.invoice_number} className="border-b border-border/50" data-testid={`cust-inv-${i.invoice_number}`}>
                  <td className="px-4 py-2.5 metric text-primary">{i.invoice_number}</td>
                  <td className="px-4 py-2.5 metric text-xs">{fmtDT(i.invoice_date)}</td>
                  <td className="px-4 py-2.5 metric text-xs">{i.order_number}</td>
                  <td className="px-4 py-2.5 metric">{money(i.taxable_amount)}</td>
                  <td className="px-4 py-2.5 metric">{money(i.gst)}</td>
                  <td className="px-4 py-2.5 metric font-bold">{money(i.invoice_total)}</td>
                  <td className="px-4 py-2.5 metric text-signal-green">{money(i.received_amount)}</td>
                  <td className="px-4 py-2.5 metric text-signal-amber">{money(i.balance)}</td>
                  <td className="px-4 py-2.5 text-xs uppercase">{i.payment_status}</td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No invoices yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "Outstanding" && outstanding && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="customer-outstanding">
          {[["Total Invoiced", outstanding.total_invoiced], ["Total Received", outstanding.total_received],
            ["Total Outstanding", outstanding.total_outstanding], ["Credit Limit", outstanding.credit_limit],
            ["Available Credit", outstanding.available_credit], ["Credit Days", outstanding.credit_days]].map(([l, v]) => (
            <div key={l} className="bg-card border border-border rounded-lg p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{l}</p>
              <p className={`metric text-2xl font-bold mt-1 ${outstandingValueClass(l, v, outstanding.credit_exceeded)}`}
                data-testid={`outstanding-${l.toLowerCase().replace(/ /g, "-")}`}>
                {l === "Credit Days" ? v : `₹${money(v)}`}
              </p>
            </div>
          ))}
          {outstanding.credit_exceeded && <p className="col-span-full text-signal-red text-sm font-semibold" data-testid="credit-exceeded-flag">Credit limit exceeded</p>}
        </div>
      )}

      {tab === "Audit Trail" && (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="customer-audit-table">
            <Th cols={AUDIT_COLS} />
            <tbody>
              {audit.map((a) => (
                <tr key={`${a.at}-${a.field}-${a.user_name}`} className="border-b border-border/50">
                  <td className="px-4 py-2.5 metric text-xs">{fmtDT(a.at)}</td>
                  <td className="px-4 py-2.5 text-xs">{a.user_name}</td>
                  <td className="px-4 py-2.5 text-xs uppercase">{a.role}</td>
                  <td className="px-4 py-2.5 metric text-xs">{a.field}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-48 truncate">{typeof a.old === "object" ? JSON.stringify(a.old) : String(a.old ?? "—")}</td>
                  <td className="px-4 py-2.5 text-xs max-w-48 truncate">{typeof a.new === "object" ? JSON.stringify(a.new) : String(a.new ?? "—")}</td>
                </tr>
              ))}
              {audit.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No changes recorded</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
